import * as React from 'react';
import onClickOutside from 'react-onclickoutside';
import classnames from 'classnames';

import Searchbox from '@components/Searchbox';

import { getBoundingClientRect } from '@utils/WindowUtils';

import './style.scss';

export interface IMultiLevelSearchDropdownProps {
  name: string;
  label: string;
  value: any;
  options: Array<{
    value: any;
    name: string;
    isDisabled?: boolean;
  }>;
  selectedOptions?: Array<{ value: any; label: string; icon?: any }>;
  onChange: TButtonOnClick;
  onBlur?: TButtonOnClick;
  onFocus?: TButtonOnClick;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: IFieldValidation;
  width?: string;
  title?: string;
  searchMode?: boolean;
  multi?: boolean;
  searchPlaceholder?: string;
  shouldHighlightMatchingSubstring?: boolean;
  onSubmit?: any;
  className?: string;
  type?: DropdownType;
  showHeader?: any;
  lastAssignees?: any[];
  allAssignees?: any[];
}

interface IMultiLevelSearchDropdownState {
  show: boolean;
  query: string;
  isLoading: boolean;
  matchedAssignees: any;
}

export class MultiLevelSearchDropdown extends React.Component<
  IMultiLevelSearchDropdownProps,
  IMultiLevelSearchDropdownState
> {
  public static defaultProps = {
    name: '',
    value: '',
    label: '',
    clearable: false,
    onChange: null,
    placeholder: 'Select Option',
    options: null,
    disabled: false,
    hasSelectAll: false,
    multi: false,
    searchMode: false,
    shouldHighlightMatchingSubstring: true,
    type: DropdownType.NO_BORDER,
    count: null,
    onLoadMoreData: (params: ILoadMoreParams) => null,
  };

  constructor(props: IMultiLevelSearchDropdownProps) {
    super(props);
    this.state = this.getInitState();
  }

  getInitState = () => ({
    show: false,
    query: '',
    isLoading: false,
    matchedAssignees: [],
  });

  toggleDropdown = (e?: React.SyntheticEvent<any>) => {
    if (e) {
      e.preventDefault();
    }

    this.setState((prevState) => ({
      show: !this.props.disabled && !prevState.show,
      query: '',
    }));
  };

  renderSelector = () => {
    const { options, value, label, placeholder, multi, disabled, required } = this.props;

    const option = options.find((elem) => elem.value.id === value);
    const filterLabel = !multi
      ? option
        ? option.value.label
        : ''
      : value
          .reduce((acc, curr) => {
            const existingObj = options.find((elem) => elem.value.id === curr);

            if (existingObj) {
              acc.push(existingObj.value.label);
            }

            return acc;
          }, []) // tslint:disable-line
          .join(', ');

    return (
      <div className={classnames('dropdown__selector', { disabled, required })} onClick={this.toggleDropdown}>
        {label && <label className={classnames('dropdown__selector__label')}>{label}</label>}
        <div onClick={this.clearSuggestions} className={classnames('dropdown__selector__selected')}>
          <label>{filterLabel || placeholder}</label>
          <span className={classnames('dropdown__arrow')} />
        </div>
      </div>
    );
  };

  handleClickOutside = (e: React.SyntheticEvent<any>) => {
    if (!this.state.show) {
      this.setState({ show: false, query: '' });
    }
  };

  onSearchQueryChange = (newSearchQuery: string) => {
    this.setState({
      query: newSearchQuery,
      ...(!newSearchQuery.trim() && { matchedAssignees: [] }),
    });
  };

  renderSuggestion = (suggestionElement: JSX.Element, suggestion: any) => {
    const iconURL = suggestion ? suggestion.iconURL : null;

    return (
      <div>
        {suggestionElement}
        {iconURL && (
          <div className={classnames('icon-chooser')}>
            <div
              className={classnames('icon-chooser__icon')}
              style={{
                background: `url(${iconURL}) center / contain no-repeat`,
              }}
            />
          </div>
        )}
      </div>
    );
  };

  searchSuggestions = (): ISearchOptions[] => [
    {
      items: this.state.matchedAssignees.map((elem) => ({
        name: `${elem.name}`,
        value: elem.value.id,
        icon: {
          iconURL: elem.iconURL,
        },
      })),
      title: 'Matching Assignees',
    },
  ];

  clearSuggestions = () => this.setState({ matchedAssignees: [] });

  getBestMatchSortedList = (searchQuery) => {
    const getAssigneeListLabel = (elem) => `${elem.value.label}`;
    const getIndexOf = (elem) => getAssigneeListLabel(elem).toLowerCase().indexOf(searchQuery.toLowerCase());

    const sortByBestMatch = (curr, next) =>
      (getIndexOf(curr) === 0 && getIndexOf(next) === 0) || (getIndexOf(curr) !== 0 && getIndexOf(next) !== 0)
        ? getAssigneeListLabel(curr).toLowerCase() < getAssigneeListLabel(next).toLowerCase()
          ? -1
          : 1
        : getAssigneeListLabel(next).toLowerCase().indexOf(searchQuery.toLowerCase()) === 0
        ? 1
        : -1;

    return sortByBestMatch;
  };

  searchAssignees = (searchQuery: string) => {
    let matchedAssignees = this.props.allAssignees || [];
    const sortByBestMatch = this.getBestMatchSortedList(searchQuery);

    const escape = (str: string) => str.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1');
    if (searchQuery.trim().length > 0) {
      matchedAssignees = this.props.allAssignees
        ? this.props.allAssignees
            .filter((elem) => RegExp(escape(searchQuery), 'ig').test(`${elem.value.label}`))
            .sort(sortByBestMatch)
        : [];
    }

    this.setState({
      matchedAssignees,
    });
  };

  onSuggestionSelect = (query: string, newAssignee: any) => {
    const value = newAssignee && newAssignee.id ? newAssignee.id : newAssignee;

    this.props.onChange({
      target: { name: this.props.name, value },
      currentTarget: { name: this.props.name, value },
    });
    this.onSearchQueryChange('');
  };

  renderDropdown = () => {
    const { searchMode, allAssignees, lastAssignees } = this.props;
    const { query } = this.state;

    return (
      <div className={classnames('dropdown__list')}>
        {this.props.showHeader ? this.props.showHeader() : ''}
        {searchMode && (
          <div
            className={classnames('search', {
              'search--searching': query.trim(),
            })}
          >
            <Searchbox
              // tslint:disable-next-line:max-line-length
              placeholder="Start typing name of the assignee"
              query={this.state.query}
              onQueryChange={this.onSearchQueryChange}
              renderSuggestion={this.renderSuggestion}
              multiSection={true}
              onFocus={this.props.onFocus}
              search={this.searchAssignees}
              onSuggestionSelect={this.onSuggestionSelect}
              searchResults={this.searchSuggestions()}
              clearSuggestions={this.clearSuggestions}
              alwaysRenderSuggestions={!!lastAssignees.length}
              defaultSuggestions={[
                {
                  title: 'Last Assigned Users',
                  items: lastAssignees,
                },
                {
                  title: 'All Users',
                  items: allAssignees,
                },
              ]}
              emptyMessage="No matching Assignees"
              matchQueryInResultAfter={2}
            />
          </div>
        )}
      </div>
    );
  };

  render() {
    const { width, className, value, error, type } = this.props;
    const boundingClientRect = getBoundingClientRect('.dropdown__wrapper');
    const style = boundingClientRect
      ? {
          maxWidth: width || boundingClientRect.width,
        }
      : null;

    const optionSelected = Array.isArray(value) ? value.length : value;

    return (
      <div
        style={style}
        className={classnames(`dropdown__wrapper ${className ? className : ''}`, {
          'dropdown__wrapper--open': this.state.show,
          'dropdown__wrapper--box': type === DropdownType.BORDER,
          'option-selected': optionSelected,
          'dropdown--error':
            error && (error.errorState === IValidationState.ERROR || error.errorState === IValidationState.WARNING),
        })}
      >
        {this.renderSelector()}
        {error && (
          <span className="field__error">
            {error.errorState === IValidationState.ERROR || error.errorState === IValidationState.WARNING
              ? error.errorMessage
              : ''}
          </span>
        )}
        {this.renderDropdown()}
      </div>
    );
  }
}

export default onClickOutside(MultiLevelSearchDropdown);
